import React from 'react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { ShoppingCart, Plus, Minus, Trash2, Waves } from 'lucide-react';

export const CartDrawer = () => {
  const { 
    items, isOpen, setIsOpen, 
    updateQuantity, removeItem, 
    subtotal, deliveryFee, total, 
    isBeachMode, setIsBeachMode,
    restaurantName, distanceKm
  } = useCart();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsOpen(false);
    navigate('/checkout');
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-[#004E64]">
            <ShoppingCart className="text-[#FF6B35]" />
            {t('cart')}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
            <div className="w-24 h-24 rounded-full bg-[#F4F1EA] flex items-center justify-center">
              <ShoppingCart size={40} className="text-[#9FA2B4]" />
            </div>
            <p className="text-[#9FA2B4] font-medium">{t('emptyCart')}</p>
            <Button 
              onClick={() => setIsOpen(false)}
              className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
            >
              {t('continueShopping')}
            </Button>
          </div>
        ) : (
          <>
            {/* Beach Mode Toggle */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#2EC4B6]/10 to-[#2EC4B6]/5 rounded-2xl mt-4">
              <div className="flex items-center gap-3">
                <Waves className="text-[#2EC4B6]" />
                <div>
                  <p className="font-semibold text-[#004E64]">{t('beachMode')}</p>
                  <p className="text-sm text-[#9FA2B4]">+€2.50 delivery</p>
                </div>
              </div>
              <Switch 
                checked={isBeachMode} 
                onCheckedChange={setIsBeachMode}
                data-testid="beach-mode-toggle"
              />
            </div>

            {/* Restaurant Name */}
            {restaurantName && (
              <p className="text-sm text-[#9FA2B4] mt-4">
                From: <span className="font-semibold text-[#004E64]">{restaurantName}</span>
              </p>
            )}

            {/* Cart Items */}
            <ScrollArea className="flex-1 mt-4">
              <div className="space-y-4 pr-4">
                {items.map(item => (
                  <div key={item.item_id} className="flex gap-4 p-3 bg-white rounded-xl border border-[#004E64]/5">
                    <div className="flex-1">
                      <h4 className="font-semibold text-[#004E64]">{item.name}</h4>
                      <p className="text-[#FF6B35] font-bold">€{item.price.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="w-8 h-8 rounded-full"
                        onClick={() => updateQuantity(item.item_id, item.quantity - 1)}
                        data-testid={`decrease-qty-${item.item_id}`}
                      >
                        <Minus size={14} />
                      </Button>
                      <span className="w-8 text-center font-semibold">{item.quantity}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="w-8 h-8 rounded-full"
                        onClick={() => updateQuantity(item.item_id, item.quantity + 1)}
                        data-testid={`increase-qty-${item.item_id}`}
                      >
                        <Plus size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-8 h-8 rounded-full text-red-500 hover:bg-red-50"
                        onClick={() => removeItem(item.item_id)}
                        data-testid={`remove-item-${item.item_id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Summary */}
            <div className="mt-4 space-y-3">
              <Separator />
              <div className="flex justify-between text-[#004E64]">
                <span>{t('subtotal')}</span>
                <span className="font-semibold">€{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-[#004E64]">
                <span className="flex items-center gap-2">
                  {t('deliveryFee')}
                  {isBeachMode && <Waves size={14} className="text-[#2EC4B6]" />}
                </span>
                <span className="font-semibold">
                  {distanceKm != null
                    ? `€${deliveryFee.toFixed(2)}`
                    : t('deliveryFrom')}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between text-[#004E64] text-lg">
                <span className="font-bold">{t('total')}</span>
                <span className="font-bold text-[#FF6B35]">
                  {distanceKm != null
                    ? `€${total.toFixed(2)}`
                    : `${t('fromPrefix')} €${total.toFixed(2)}`}
                </span>
              </div>
              <Button 
                onClick={handleCheckout}
                className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full py-6 text-lg font-bold"
                data-testid="checkout-button"
              >
                {t('checkout')}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
